'use client';
// import Image from 'next/image';
import { SolitoImage as Image } from 'solito/image'
import { useCallback, useEffect, useRef, useState } from 'react';
import type { LayoutChangeEvent, NativeScrollEvent, NativeSyntheticEvent } from 'react-native';
import {
  AnimatePresence,
  Circle,
  ScrollView,
  Stack,
  type TamaguiElement,
  XStack,
  YStack,
} from 'tamagui';

type ImageCarouselProps = {
  images: any[];
  interval?: number;
  onImagePress?: (item: any, index: number) => void;
  autoPlayInterval?: number;
  dotSize?: number;
  containerStyle?: any;
};

type ScrollableTamaguiElement = TamaguiElement & {
  scrollTo?: (options: { x?: number; y?: number; animated?: boolean }) => void;
};

export function CustomizedCarousel({ images, onImagePress, interval = 3000 }: ImageCarouselProps) {
  const containerRef = useRef<TamaguiElement | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  // const media = useMedia();

  const scrollToIndex = useCallback(
    (index: number) => {
      const container = containerRef.current as ScrollableTamaguiElement | null;
      if (!container || !containerWidth) return;

      container.scrollTo?.({ x: index * containerWidth, animated: true });
      setActiveIndex(index);
    },
    [containerWidth]
  );

  const onLayout = (event: LayoutChangeEvent) => {
    setContainerWidth(event.nativeEvent.layout.width);
  };

  const onScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (!containerWidth || images.length === 0) return;

    const nextIndex = Math.min(
      Math.max(Math.round(event.nativeEvent.contentOffset.x / containerWidth), 0),
      images.length - 1
    );
    setActiveIndex(nextIndex);
  };

  useEffect(() => {
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated || images.length === 0 || paused) return;

    const timer = setInterval(() => {
      const next = (activeIndex + 1) % images.length;
      scrollToIndex(next);
    }, interval);

    return () => clearInterval(timer);
  }, [activeIndex, hydrated, paused, images.length, interval, scrollToIndex]);

  // const imageHeight = media.sm ? 160 : media.md ? 200 : 220;

  return (
    <YStack
      width="100%"
      alignItems="center"
      space="$2"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <ScrollView
        ref={(node) => {
          containerRef.current = node as unknown as TamaguiElement | null;
        }}
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        onLayout={onLayout}
        onScroll={onScroll}
        scrollEventThrottle={16}
        snapToInterval={containerWidth || undefined}
        decelerationRate="fast"
        width="100%"
        maxWidth={600}
      >
        {images.map((src, index) => (
          <YStack
            key={index}
            width={containerWidth || '100%'}
            height={200}
            minHeight={200}
            flexShrink={0}
            position="relative"
            borderRadius="$4"
            overflow="hidden"
            paddingHorizontal={8}
          >
            <AnimatePresence>
              {index === activeIndex && (
                <Stack
                  animation="quick"
                  enterStyle={{ opacity: 1 }}
                  exitStyle={{ opacity: 0.5 }}
                  opacity={1}
                  width="100%"
                  height="100%"
                  onPress={() => onImagePress?.(src, index)}
                  aspectRatio={3 / 2}
                >
                  <Image
                    src={src?.url}
                    alt={`Image ${index + 1}`}
                    fill
                    sizes="(max-width: 600px) 100vw, 600px"
                    style={{
                      objectFit: 'contain',
                      width: '100%',
                      height: '100%',
                      maxWidth: 600,
                    }}
                    priority={index === 0}
                  />
                </Stack>
              )}
            </AnimatePresence>
          </YStack>
        ))}
      </ScrollView>

      {/* Pagination */}
      {images.length > 1 && (
        <XStack gap="$2" justifyContent="center">
          {images.map((_, i) => (
            <Circle
              key={i}
              marginBottom={4}
              size={'$0.75'}
              backgroundColor={i === activeIndex ? '$primary' : '$gray'}
              onPress={() => scrollToIndex(i)}
              cursor="pointer"
            />
          ))}
        </XStack>
      )}
    </YStack>
  );
}
